import { isFeatureEnabled } from 'owa-feature-flags';
import type { RibbonId } from 'owa-ribbon-ids';
import {
    FormatControlId,
    MailRibbonControlId,
    MailRibbonGroupId,
    MailRibbonTabId,
} from 'owa-ribbon-ids';

/**
 * This maps a RibbonId to its specific keytip string content, notably *not* its entire keytip sequence.
 * Note that this does *not* return IKeytipProps, which is the type that eventually Ribbon consumes.
 * To construct a IKeytipProps, please @see {constructKeytip} from @see {keytipUtils.ts}.
 */
let ribbonIdKeytipStringMap: Map<RibbonId, string>;

export const getKeytipString = (ribbonId: RibbonId): string => {
    if (!isFeatureEnabled('mon-ribbon-keytips')) {
        return '';
    }

    if (!ribbonIdKeytipStringMap) {
        initializeRibbonKeytipStringMap();
    }

    // If we can't find a RibbonId but we're calling `getKeytipString` on it, chances are it was
    // an oversight and we need to add it to the map below. Throw error in this case.

    if (!ribbonIdKeytipStringMap.has(ribbonId)) {
        throw new Error('RibbonId: ' + ribbonId + ' not found in ribbonIdKeytipStringMap');
        return '';
    }

    return ribbonIdKeytipStringMap.get(ribbonId)!;
};

/**
 * Initializes map that maps RibbonIds to its specific keytip string.
 */
function initializeRibbonKeytipStringMap() {
    ribbonIdKeytipStringMap = new Map();

    ribbonIdKeytipStringMap
        .set(FormatControlId.AlignCenter, 'AC')
        .set(FormatControlId.AlignLeft, 'AL')
        .set(FormatControlId.AlignRight, 'AR')
        .set(FormatControlId.Bold, '1')
        .set(FormatControlId.Bullets, 'U')
        .set(FormatControlId.CapitalizeEachWord, 'C')
        .set(FormatControlId.ChangeCase, '7')
        .set(FormatControlId.Copy, 'C')
        .set(FormatControlId.Cut, 'T')
        .set(FormatControlId.FontBackColorPicker, 'I')
        .set(FormatControlId.FontColorPicker, 'FC')
        .set(FormatControlId.FontNamePicker, 'FF')
        .set(FormatControlId.FontSizePicker, 'FS')
        .set(FormatControlId.FormatPainter, 'FP')
        .set(FormatControlId.Indent, 'AI')
        .set(FormatControlId.Italic, '2')
        .set(FormatControlId.LeftToRight, 'LT')
        .set(FormatControlId.Lowercase, 'L')
        .set(FormatControlId.MoreColor, 'M')
        .set(FormatControlId.NoColor, 'N')
        .set(FormatControlId.Numbering, 'N')
        .set(FormatControlId.Outdent, 'AO')
        .set(FormatControlId.Paste, 'V')
        .set(FormatControlId.PasteCutCopy, 'V')
        .set(FormatControlId.Quote, 'QT')
        .set(FormatControlId.Redo, 'R')
        .set(FormatControlId.RemoveFormat, 'E')
        .set(FormatControlId.RightToLeft, 'RL')
        .set(FormatControlId.SentenceCase, 'S')
        .set(FormatControlId.StrikeThrough, 'G')
        .set(FormatControlId.Subscript, 'SB')
        .set(FormatControlId.Superscript, 'SP')
        .set(FormatControlId.Underline, '3')
        .set(FormatControlId.Undo, 'U')
        .set(FormatControlId.UndoRedo, 'AZ')
        .set(FormatControlId.Uppercase, 'U')
        .set(MailRibbonControlId.AddressBook, 'AB')
        .set(MailRibbonControlId.AlwaysMoveToFocusedInbox, 'AF')
        .set(MailRibbonControlId.AlwaysMoveToOtherInbox, 'AO')
        .set(MailRibbonControlId.Archive, 'O')
        .set(MailRibbonControlId.AssignPolicy, 'AP')
        .set(MailRibbonControlId.AssignPolicyArchiveInherit, 'AU')
        .set(MailRibbonControlId.AssignPolicyDeleteInherit, 'U')
        .set(MailRibbonControlId.AttachFile, 'AF')
        .set(MailRibbonControlId.AttachFileFromComputer, 'B')
        .set(MailRibbonControlId.AttachFileFromOnlineLocation, 'C')
        .set(MailRibbonControlId.AttachFileFromUpload, 'U')
        .set(MailRibbonControlId.AttachSig, 'AS')
        .set(MailRibbonControlId.Block, 'B')
        .set(MailRibbonControlId.BrowseGroups, 'B')
        .set(MailRibbonControlId.Categorize, 'G')
        .set(MailRibbonControlId.CategorizeAllCategories, 'A')
        .set(MailRibbonControlId.CategorizeClearCategories, 'C')
        .set(MailRibbonControlId.CategorizeManageCategories, 'M')
        .set(MailRibbonControlId.CategorizeNewCategory, 'N')
        .set(MailRibbonControlId.CategorizeSearchBox, 'S')
        .set(MailRibbonControlId.CheckForAccessibilityIssues, 'AC')
        .set(MailRibbonControlId.CheckNames, 'M')
        .set(MailRibbonControlId.Copy, 'C')
        .set(MailRibbonControlId.CreateRule, 'CR')
        .set(MailRibbonControlId.Cut, 'T')
        .set(MailRibbonControlId.DeleteItem, 'D')
        .set(MailRibbonControlId.Dictate, 'D')
        .set(MailRibbonControlId.DiscardDraft, 'D')
        .set(MailRibbonControlId.Emojis, 'E')
        .set(MailRibbonControlId.Encrypt, 'PM')
        .set(MailRibbonControlId.Encrypt_SingleButton, 'PM')
        .set(MailRibbonControlId.FlagClearFlag, 'E')
        .set(MailRibbonControlId.FlagCompleteNextWeek, 'N')
        .set(MailRibbonControlId.FlagCompleteNoDate, 'A')
        .set(MailRibbonControlId.FlagCompleteThisWeek, 'W')
        .set(MailRibbonControlId.FlagCompleteToday, 'T')
        .set(MailRibbonControlId.FlagCompleteTomorrow, 'O')
        .set(MailRibbonControlId.FlagMarkComplete, 'M')
        .set(MailRibbonControlId.FlagUnflag, 'U')
        .set(MailRibbonControlId.FormatPainter, 'FP')
        .set(MailRibbonControlId.GetAddIns, 'AS')
        .set(MailRibbonGroupId.Group_Find, 'ZN')
        .set(MailRibbonGroupId.Group_Illustrations, 'ZI')
        .set(MailRibbonGroupId.Group_Include, 'ZC')
        .set(MailRibbonGroupId.Group_Links, 'ZL')
        .set(MailRibbonGroupId.Group_Move, 'ZA')
        .set(MailRibbonGroupId.Group_Names, 'ZN')
        .set(MailRibbonGroupId.Group_QuickSteps, 'QS')
        .set(MailRibbonGroupId.Group_Tags, 'ZT')
        .set(MailRibbonGroupId.Group_Tables, 'ZT')
        .set(MailRibbonControlId.HighImportance, 'H')
        .set(MailRibbonControlId.IgnoreStopIgnore, 'X')
        .set(MailRibbonControlId.ImmersiveReader, 'L')
        .set(MailRibbonControlId.JunkFlyout, 'J')
        .set(MailRibbonControlId.JunkNotJunk, 'J')
        .set(MailRibbonControlId.LowImportance, 'L')
        .set(MailRibbonControlId.ManageRules, 'M')
        .set(MailRibbonControlId.MoreRetentionPolicies, 'M')
        .set(MailRibbonControlId.Move, 'MV')
        .set(MailRibbonControlId.MoveAllMessagesFrom, 'AL')
        .set(MailRibbonControlId.MoveToCreateNewFolder, 'NF')
        .set(MailRibbonControlId.MoveToDifferentFolder, 'D')
        .set(MailRibbonControlId.MoveToFocusedInbox, 'F')
        .set(MailRibbonControlId.MoveToOtherInbox, 'O')
        .set(MailRibbonControlId.NewContact, 'C')
        .set(MailRibbonControlId.NewEvent, 'E')
        .set(MailRibbonControlId.NewGroup, 'U')
        .set(MailRibbonControlId.NewMessage, 'M')
        .set(MailRibbonControlId.NewMessageSplitButton, 'N')
        .set(MailRibbonControlId.NewNote, 'N')
        .set(MailRibbonControlId.NewTask, 'T')
        .set(MailRibbonControlId.NewYammerPost, 'P')
        .set(MailRibbonControlId.NormalImportance, 'N')
        .set(MailRibbonControlId.Paste, 'V')
        .set(MailRibbonControlId.Phishing, 'P')
        .set(MailRibbonControlId.Pictures, 'PC')
        .set(MailRibbonControlId.PinUnpin, 'Y')
        .set(MailRibbonControlId.Poll, 'PL')
        .set(MailRibbonControlId.Print, 'P')
        .set(MailRibbonControlId.ReadAloud, 'L')
        .set(MailRibbonControlId.ReadUnread, 'W')
        .set(MailRibbonControlId.ReplyWithMeeting, 'E')
        .set(MailRibbonControlId.RespondForward, 'FW')
        .set(MailRibbonControlId.RespondReply, 'RP')
        .set(MailRibbonControlId.RespondReplyAll, 'RA')
        .set(MailRibbonControlId.RibbonCustomizer, 'CZ')
        .set(MailRibbonControlId.Rules, 'RL')
        .set(MailRibbonControlId.Save, 'SD')
        .set(MailRibbonControlId.SendDraft, 'SE')
        .set(MailRibbonControlId.Sensitivity, 'AY')
        .set(MailRibbonControlId.Sensitivity_LearnMore, 'L')
        .set(MailRibbonControlId.SetImportance, 'I')
        .set(MailRibbonControlId.ShowHideFrom, 'F')
        .set(MailRibbonControlId.ShowLinkDialog, 'L')
        .set(MailRibbonControlId.ShowMessageOptions, 'O')
        .set(MailRibbonControlId.SignatureOptions, 'S')
        .set(MailRibbonControlId.Snooze, 'SZ')
        .set(MailRibbonControlId.SnoozeChooseDate, 'C')
        .set(MailRibbonControlId.SnoozeLaterToday, 'T')
        .set(MailRibbonControlId.SnoozeNextWeek, 'N')
        .set(MailRibbonControlId.SnoozeThisEvening, 'E')
        .set(MailRibbonControlId.SnoozeTomorrow, 'O')
        .set(MailRibbonControlId.SnoozeWeekend, 'W')
        .set(MailRibbonControlId.Sweep, 'SP')
        .set(MailRibbonControlId.SwitchDraftFormat, 'T')
        .set(MailRibbonControlId.Undo, 'Z')
        .set(MailRibbonControlId.Unsnooze, 'US')
        .set(MailRibbonControlId.Table, 'T')
        .set(MailRibbonTabId.Tab_Format, 'O')
        .set(MailRibbonTabId.Tab_Home, 'H')
        .set(MailRibbonTabId.Tab_Insert, 'N')
        .set(MailRibbonTabId.Tab_Message, 'M')
        .set(MailRibbonTabId.Tab_View, 'V');
}
